import React, { useEffect, useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  Assessment,
  Refresh,
  People,
  Business,
  TrendingUp,
  CalendarMonth,
  Description,
  CloudDownload,
} from "@mui/icons-material";

const ReportCard = ({ title, description, icon, color, onDownload }) => (
  <Card sx={{ borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Avatar sx={{ bgcolor: `${color}15`, width: 48, height: 48 }}>
          {React.cloneElement(icon, { sx: { color: color } })}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CloudDownload />}
        onClick={onDownload}
        sx={{ borderColor: color, color: color, "&:hover": { borderColor: color, bgcolor: `${color}08` } }}
      >
        Download Report
      </Button>
    </CardContent>
  </Card>
);

const AdminReports = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading, GetAdminDetail } = useSelector((state) => state.getAdminDetail);
  const stats = GetAdminDetail;

  const [selectedPeriod, setSelectedPeriod] = useState("month");

  useEffect(() => {
    dispatch(GetAdmin());
  }, [dispatch]);

  const handleDownloadReport = (reportType) => {
    // Placeholder - Backend will provide report generation
    alert(`${reportType} report download - Backend API integration required`);
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={200} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Skeleton variant="text" width={150} height={30} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ textAlign: "center" }}>
                <Skeleton variant="text" width={60} height={45} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width={80} height={20} sx={{ mx: "auto" }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Skeleton variant="text" width={150} height={30} sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Card sx={{ borderRadius: 2, p: 2 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="80%" height={24} />
                  <Skeleton variant="text" width="60%" height={18} />
                </Box>
              </Box>
              <Skeleton variant="rounded" width="100%" height={36} />
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? <LoadingSkeleton /> : (
        <>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Reports & Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate and download platform reports
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => dispatch(GetAdmin())}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                  {stats?.user_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                  {stats?.landlord_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Landlords
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                  {stats?.tenant_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tenants
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#e94560" }}>
                  {stats?.property_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Properties
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Available Reports */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Available Reports
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="User Report"
              description="Complete list of all users including landlords and tenants"
              icon={<People />}
              color="#6366f1"
              onDownload={() => handleDownloadReport("User")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Property Report"
              description="All registered properties with approval status"
              icon={<Business />}
              color="#e94560"
              onDownload={() => handleDownloadReport("Property")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Landlord Report"
              description="Landlord registrations and verification status"
              icon={<Description />}
              color="#22c55e"
              onDownload={() => handleDownloadReport("Landlord")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Tenant Report"
              description="All tenant accounts and their activity"
              icon={<People />}
              color="#f59e0b"
              onDownload={() => handleDownloadReport("Tenant")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Revenue Report"
              description="Financial transactions and platform earnings"
              icon={<TrendingUp />}
              color="#10b981"
              onDownload={() => handleDownloadReport("Revenue")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Monthly Summary"
              description="Monthly overview of platform activities"
              icon={<CalendarMonth />}
              color="#8b5cf6"
              onDownload={() => handleDownloadReport("Monthly")}
            />
          </Grid>
        </Grid>

        {/* Report Schedule Notice */}
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: "#f0fdf4",
            border: "1px solid #86efac",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Avatar sx={{ bgcolor: "#22c55e15" }}>
              <Assessment sx={{ color: "#22c55e" }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#166534", mb: 1 }}>
                Report Generation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reports will be generated in Excel/CSV format once the backend API endpoints are implemented.
                The backend developer will need to create endpoints for generating downloadable reports
                with filtered data based on the selected time period.
              </Typography>
            </Box>
          </Box>
        </Paper>
        </>
        )}
      </Box>
    </>
  );
};

export default AdminReports;
